import { Checkbox, Input, InputNumber, Radio, Slider, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { InferenceOptionsOverrideEditor } from "@/components/ai-connection/InferenceOptionsOverrideEditor";
import {
  getOverrideOption,
  setOverrideOption,
} from "@/components/ai-connection/inferenceOptionsOverride";

export interface JudgingValues {
  enabled: boolean;
  mode: "ground_truth" | "image";
  override: Record<string, any> | null;
  aiProfileId: string | null;
  instructions: string;
  criteria: string;
}

export const DEFAULT_JUDGING: JudgingValues = {
  enabled: false,
  mode: "ground_truth",
  override: null,
  aiProfileId: null,
  instructions: "",
  criteria: "",
};

// The judge overrides its base connection exactly like the agent: generation options (e.g.
// temperature) live in override.options. When no override is given the backend falls back to a
// hardcoded Mistral judge at temperature 0.1 (see backend BenchmarkBackendResolver / BenchmarkJudge).
const DEFAULT_JUDGE_TEMPERATURE = 0.1;

interface Props {
  values: JudgingValues;
  onChange: (patch: Partial<JudgingValues>) => void;
  // Image-mode judging only applies to image runs; mail digest etc. judge against ground truth only.
  allowImageMode?: boolean;
}

const LabelWithTooltip = ({
  label,
  tooltipTitle,
}: {
  label: string;
  tooltipTitle: string;
}) => (
  <div className="flex items-center gap-2 mb-1.5">
    <label className="block text-sm font-semibold text-gray-800">{label}</label>
    <Tooltip
      title={tooltipTitle}
      color="blue"
      styles={{ root: { width: "300px" } }}
    >
      <InfoCircleOutlined className="text-blue-500 hover:text-blue-700 cursor-help" />
    </Tooltip>
  </div>
);

const SliderField = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  tooltipTitle,
  description,
}: any) => (
  <div className="mb-4 bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
    <LabelWithTooltip label={label} tooltipTitle={tooltipTitle} />
    {description && (
      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        {description}
      </p>
    )}
    <div className="flex items-center gap-5">
      <Slider
        min={min}
        max={max}
        step={step}
        value={typeof value === "number" ? value : min}
        onChange={(v) => onChange(v)}
        className="flex-grow m-0"
      />
      <InputNumber
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(v) => onChange(v)}
        className="w-20 bg-gray-50 border-gray-200 shadow-inner rounded-md font-mono text-center"
      />
    </div>
  </div>
);

export const BenchmarkJudgingStep = ({
  values,
  onChange,
  allowImageMode = true,
}: Props) => {
  return (
    <div>
      <Checkbox
        checked={values.enabled}
        onChange={(e) => onChange({ enabled: e.target.checked })}
      >
        Judge results with an LLM
      </Checkbox>

      {values.enabled && (
        <div className="mt-4 space-y-4 pl-6 border-l-2 border-blue-100">
          {allowImageMode && (
            <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
              <LabelWithTooltip
                label="Judge mode"
                tooltipTitle="Determines whether the judge evaluates the text against a ground truth file, or evaluates if the text accurately describes the image (requires a vision-capable judge model)."
              />
              <Radio.Group
                value={values.mode}
                onChange={(e) => onChange({ mode: e.target.value })}
              >
                <Radio value="ground_truth">
                  Compare to ground truth (expected.json)
                </Radio>
                <Radio value="image">Judge the image directly</Radio>
              </Radio.Group>
            </div>
          )}

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm transition-all">
            <InferenceOptionsOverrideEditor
              label="Judge AI Profile & Inference Overrides"
              help="Select an AI Profile for the Judge, or override inference options (e.g. temperature). The model and credentials always come from the selected profile. If blank, the hardcoded Mistral judge will be used."
              value={values.override}
              profileId={values.aiProfileId}
              onChangeOverride={(override) => onChange({ override })}
              onChangeProfile={(aiProfileId) => onChange({ aiProfileId })}
              profileFilter={
                values.mode === "image" ? (p) => !!p.hasVision : undefined
              }
            />

            {values.mode === "image" && (
              <p className="text-xs text-amber-600 mt-1.5">
                Image judging needs a vision-capable model — this list is
                filtered to Mistral's vision models.
              </p>
            )}
          </div>

          <SliderField
            label="Temperature"
            value={
              getOverrideOption(values.override, "temperature") ??
              DEFAULT_JUDGE_TEMPERATURE
            }
            onChange={(v: number | null) =>
              onChange({
                override: setOverrideOption(values.override, "temperature", v),
              })
            }
            min={0}
            max={1}
            step={0.1}
            tooltipTitle="Controls the randomness of the judge's reasoning. A lower value (e.g. 0.1) makes the judge strictly factual and consistent."
            description="0.1 is recommended for reliable, deterministic scoring."
          />

          <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <LabelWithTooltip
              label="Custom instructions (optional)"
              tooltipTitle="Replaces the default system prompt ('You are an expert impartial judge...') with your own instructions."
            />
            <Input.TextArea
              rows={2}
              className="w-full bg-gray-50 border-gray-200 shadow-inner rounded-md"
              value={values.instructions}
              onChange={(e) => onChange({ instructions: e.target.value })}
              placeholder="Override the default system instructions…"
            />
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <LabelWithTooltip
              label="Custom grading criteria (optional)"
              tooltipTitle="Appends specific rules to the default 1-10 scoring scale criteria."
            />
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              {values.mode === "image"
                ? "The judge looks at each image and rates the classification on a scale of 1 to 10."
                : "The judge scores each classification against its expected.json ground truth on a scale of 1 to 10."}
              Type below to append extra criteria rules to the evaluation.
            </p>
            <Input.TextArea
              rows={2}
              className="w-full bg-gray-50 border-gray-200 shadow-inner rounded-md"
              value={values.criteria}
              onChange={(e) => onChange({ criteria: e.target.value })}
              placeholder="e.g. Penalize heavily if the output contains hallucinated metadata"
            />
          </div>

          <div className="mt-4 border border-gray-200 rounded-md p-3 bg-gray-50 overflow-hidden">
            <details className="group">
              <summary className="text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer list-none flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 transition-transform group-open:rotate-90"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                View Exact Prompt
              </summary>
              <div className="mt-3 text-xs font-mono text-gray-600 whitespace-pre-wrap bg-white border border-gray-200 p-3 rounded h-48 overflow-y-auto">
                {values.mode === "image"
                  ? `You are an expert impartial judge evaluating an AI image classification.\nLook at the attached image and the "Actual Output" the AI produced for it. Judge how well the\nclassification fits the image and score it on a scale of 1 to 10.\n\nCriteria:\n10: The classification is clearly correct for the image.\n5-7: Defensible but arguable.\n1-4: Clearly wrong for the image.\n${values.criteria ? values.criteria + "\n" : ""}\nActual Output:\n\`\`\`json\n{ ... }\n\`\`\`\n\nRespond STRICTLY with a JSON object containing two keys:\n1. "score": an integer between 1 and 10.\n2. "reasoning": a short string explaining the score.`
                  : `You are an expert impartial judge evaluating an AI classification.\nYou will be given the "Expected Output" (ground truth) and the "Actual Output" produced by the AI.\nCompare them and score the Actual Output on a scale of 1 to 10.\n\nCriteria:\n10: Exact match or semantically identical.\n5-7: Close but not exact (e.g. an adjacent/related class).\n1-4: Wrong classification.\n${values.criteria ? values.criteria + "\n" : ""}\nExpected Output:\n\`\`\`json\n{ ... }\n\`\`\`\n\nActual Output:\n\`\`\`json\n{ ... }\n\`\`\`\n\nRespond STRICTLY with a JSON object containing two keys:\n1. "score": an integer between 1 and 10.\n2. "reasoning": a short string explaining the score.`}
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
};
